import * as React from 'react'
import { FormGroup, HTMLInputProps, INumericInputProps, Position, Tag } from '@blueprintjs/core'
import VirtualizedSelect from 'react-virtualized-select'

import { ProductFormGroup } from '../ProductFormGroup'
import { renderError, stylizeTitle } from '../Helpers'
import { GutterTooltipError } from '../GutterTooltipError'
import NullSafeNumericInput from '../../../../../components/NullSafeNumericInput'

export type ChannelOnChangeHandler = ({ name, value }: { name: string; value: any }, callback?: () => void) => void

export interface InputProps {
  error?: any
  onListingChange?: ChannelOnChangeHandler
  // eslint-disable-next-line react/no-unused-prop-types
  errorTooltipOffset?: number // Eslint doesn't like how only some usages of InputProps consume errorTooltipOffset
  // eslint-disable-next-line react/no-unused-prop-types
  errorTooltipPosition?: string
  showRequiredLabel?: boolean
}

interface QuantityInputProps extends InputProps {
  helperText?: string
}

export interface SelectOption {
  label: string
  value: number | string
}
interface PriceInputProps extends InputProps {
  useCAD?: boolean
}
interface ChannelSelectInputProps extends InputProps {
  label: string
  name: string
  value?: string | number
  options: SelectOption[]
  onListingChange: ChannelOnChangeHandler
  disabled?: boolean
  helperText?: string
}
export const ChannelPriceInput = React.memo((props: PriceInputProps & INumericInputProps & HTMLInputProps) => (
  <ProductFormGroup
    title="Price"
    error={props.error}
    showRequiredLabel={props.showRequiredLabel}
    helperText={renderError(props.error)}
  >
    <GutterTooltipError isOpen={props.error} offsetPx={props.errorTooltipOffset} position={Position.LEFT}>
      <NullSafeNumericInput
        min={0.01}
        placeholder={props.placeholder}
        clampValueOnBlur
        leftIcon="dollar"
        minorStepSize={0.01}
        selectAllOnFocus
        name="price"
        onValueChange={(_, value) => props.onListingChange({ name: 'price', value })}
        value={props.value || null}
        intent={props.error ? 'danger' : 'none'}
        rightElement={props.useCAD ? <Tag minimal>CAD</Tag> : undefined}
        disabled={props.disabled}
        title={props.title}
        fill
      />
    </GutterTooltipError>
  </ProductFormGroup>
))

export const SharedQuantityInput = React.memo((props: QuantityInputProps & INumericInputProps) => (
  <FormGroup
    label={stylizeTitle('Quantity', props.error)}
    helperText={renderError(props.error) || props.helperText ? props.helperText : ''}
  >
    <GutterTooltipError isOpen={props.error} offsetPx={props.errorTooltipOffset} position={props.errorTooltipPosition}>
      <NullSafeNumericInput
        min={0}
        placeholder={props.placeholder}
        clampValueOnBlur
        minorStepSize={1}
        selectAllOnFocus
        name="quantity"
        onValueChange={(_, value) => props.onListingChange({ name: 'quantity', value })}
        value={props.value || null}
        disabled={props.disabled}
        intent={props.error ? 'danger' : 'none'}
        fill
      />
    </GutterTooltipError>
  </FormGroup>
))
export const ChannelSelectInput: React.FC<ChannelSelectInputProps> = React.memo(props => {
  const {
    label,
    name,
    value,
    options,
    onListingChange,
    error,
    errorTooltipOffset,
    errorTooltipPosition,
    disabled,
    helperText,
    showRequiredLabel
  } = props

  const selectOptions = options.map(opt => ({
    label: opt.label,
    value: String(opt.value)
  }))

  const selectedOption = String(value ?? '')

  return (
    <ProductFormGroup
      title={label}
      showRequiredLabel={showRequiredLabel}
      error={error}
      helperText={helperText || renderError(error)}
    >
      <GutterTooltipError
        isOpen={!!error}
        offsetPx={errorTooltipOffset}
        position={errorTooltipPosition || Position.LEFT}
      >
        <VirtualizedSelect
          name={name}
          value={selectedOption}
          options={selectOptions}
          onChange={selected =>
            onListingChange({
              name,
              value: selected ? parseInt(selected.value, 10) : ''
            })
          }
          disabled={disabled}
          clearable
          placeholder="Select..."
          className={error ? 'bp4-intent-danger' : ''}
        />
      </GutterTooltipError>
    </ProductFormGroup>
  )
})
